/**
 * Threshold Gift Promo v1
 * Configured via shop metafields (window.thresholdGiftConfig).
 * When cart value (after discounts, excluding this gift) exceeds threshold,
 * a free gift (qty 1) is added to cart. Removed if cart drops below.
 * Quantity is always enforced to 1.
 * Includes a dynamic progress banner in the cart.
 */

(function() {
  'use strict';

  var CONFIG = window.thresholdGiftConfig;
  if (!CONFIG || CONFIG.threshold == null || CONFIG.freeVariantId == null) {
    console.log('[THRESHOLD-GIFT] No active promo configured. Script inactive.');
    return;
  }

  // Shopify returns prices in cents (hundredths), so multiply display value by 100
  var THRESHOLD_CENTS = CONFIG.threshold * 100;

  console.log('[THRESHOLD-GIFT] Config loaded — threshold:', CONFIG.threshold, '| free variant:', CONFIG.freeVariantId);

  var isProcessing = false;
  var isSelfUpdate = false;
  var lastCartToken = null;
  var lastCartSignature = null;
  var syncTimeout = null;
  var lastCartData = null;
  var sectionChangeTimer = null;
  var bannerUpdating = false;

  // ============================================
  // CART OPERATIONS
  // ============================================

  async function getCart() {
    try {
      var response = await fetch('/cart.js', {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' }
      });
      if (!response.ok) throw new Error('Failed to fetch cart');
      return await response.json();
    } catch (error) {
      console.error('[THRESHOLD-GIFT] Error fetching cart:', error);
      return null;
    }
  }

  // Content signature: changes whenever items/quantities/total change.
  // (cart.token does NOT change between item updates, so it can't be used
  //  to detect cart changes — that made the old poll a no-op.)
  function cartSignature(cart) {
    if (!cart || !cart.items) return '0';
    var parts = cart.items.map(function(i) { return i.key + 'x' + i.quantity; });
    return cart.item_count + '|' + cart.total_price + '|' + parts.join(',');
  }

  function getCartValueExcludingGifts(cart) {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce(function(total, item) {
      if (item.variant_id === CONFIG.freeVariantId) return total;
      if (item.properties && (item.properties._threshold_gift === 'true' || item.properties._free_gift === 'true')) return total;
      return total + item.final_line_price;
    }, 0);
  }

  function getThresholdGiftInCart(cart) {
    if (!cart || !cart.items) return { quantity: 0, key: null, rogueItems: [] };
    var managedItem = null;
    var rogueItems = [];
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (item.variant_id === CONFIG.freeVariantId) {
        if (item.properties && item.properties._threshold_gift === 'true') {
          managedItem = item;
        } else if (!(item.properties && item.properties._free_gift === 'true')) {
          rogueItems.push({ key: item.key, quantity: item.quantity });
        }
      }
    }
    return {
      quantity: managedItem ? managedItem.quantity : 0,
      key: managedItem ? managedItem.key : null,
      rogueItems: rogueItems
    };
  }

  // Returns the updated full cart (from /cart/change.js response)
  async function updateCartItem(key, quantity) {
    try {
      isSelfUpdate = true;
      var response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
      if (!response.ok) throw new Error('Failed to update cart');
      return await response.json(); // This IS the full updated cart
    } catch (error) {
      console.error('[THRESHOLD-GIFT] Error updating cart:', error);
      return null;
    }
  }

  async function addToCart(variantId, quantity) {
    try {
      isSelfUpdate = true;
      var response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity,
          properties: { '_threshold_gift': 'true' }
        })
      });
      if (!response.ok) throw new Error('Failed to add to cart');
      return await response.json(); // Returns added item, NOT full cart
    } catch (error) {
      console.error('[THRESHOLD-GIFT] Error adding to cart:', error);
      return null;
    }
  }

  // ============================================
  // SYNC LOGIC (optimized: minimal network requests)
  // ============================================

  async function syncThresholdGift() {
    if (isProcessing) return { synced: false };

    isProcessing = true;
    isSelfUpdate = true;

    try {
      var cart = await getCart();
      if (!cart) {
        isProcessing = false;
        isSelfUpdate = false;
        return { synced: false };
      }

      var gift = getThresholdGiftInCart(cart);

      // Remove rogue (manually-added) copies — each returns updated cart
      for (var i = 0; i < gift.rogueItems.length; i++) {
        console.log('[THRESHOLD-GIFT] Removing rogue gift (qty:', gift.rogueItems[i].quantity, ')...');
        var updated = await updateCartItem(gift.rogueItems[i].key, 0);
        if (updated) cart = updated;
      }

      var cartValue = getCartValueExcludingGifts(cart);
      var isEligible = cartValue >= THRESHOLD_CENTS;
      var targetQty = isEligible ? 1 : 0;

      console.log('[THRESHOLD-GIFT] Cart value: $' + (cartValue / 100) + ' | Threshold: $' + CONFIG.threshold + ' | Eligible:', isEligible, '| Gift qty:', gift.quantity);

      if (gift.quantity === targetQty && gift.rogueItems.length === 0) {
        console.log('[THRESHOLD-GIFT] Already in sync');
        lastCartToken = cart.token;
        lastCartData = cart;
        updateBanner();
        isProcessing = false;
        setTimeout(function() { isSelfUpdate = false; }, 500);
        return { synced: true };
      }

      // Make the cart change — reuse response as updated cart where possible
      if (targetQty === 0 && gift.key) {
        console.log('[THRESHOLD-GIFT] Removing gift (below threshold)');
        var result = await updateCartItem(gift.key, 0);
        if (result) cart = result;
      } else if (gift.key && gift.quantity !== targetQty) {
        console.log('[THRESHOLD-GIFT] Correcting gift qty to 1');
        var result = await updateCartItem(gift.key, 1);
        if (result) cart = result;
      } else if (targetQty === 1 && !gift.key) {
        console.log('[THRESHOLD-GIFT] Adding threshold gift');
        await addToCart(CONFIG.freeVariantId, 1);
        // addToCart returns item not cart, so fetch cart once
        var freshCart = await getCart();
        if (freshCart) cart = freshCart;
      }

      lastCartToken = cart.token;
      lastCartSignature = cartSignature(cart);
      lastCartData = cart;

      // Update banner immediately (perceived speed)
      updateBanner();

      // Update cart count bubble from data we already have
      updateCartBubbleFromData(cart);

      // Refresh cart sections (parallel)
      await refreshCartUI();

      // Re-inject banner after section refresh destroyed it
      updateBanner();

      isProcessing = false;
      setTimeout(function() { isSelfUpdate = false; }, 1000);
      return { synced: true };

    } catch (error) {
      console.error('[THRESHOLD-GIFT] Sync error:', error);
      isProcessing = false;
      isSelfUpdate = false;
      return { synced: false, error: error };
    }
  }

  // ============================================
  // CHECKOUT VALIDATION (registered globally)
  // ============================================

  async function validateThresholdGift() {
    console.log('[THRESHOLD-GIFT] Validating cart before checkout...');

    var cart = await getCart();
    if (!cart) {
      console.error('[THRESHOLD-GIFT] Could not fetch cart for validation');
      return false;
    }

    var gift = getThresholdGiftInCart(cart);
    var corrected = false;

    for (var i = 0; i < gift.rogueItems.length; i++) {
      var result = await updateCartItem(gift.rogueItems[i].key, 0);
      if (result) cart = result;
      corrected = true;
    }

    var cartValue = getCartValueExcludingGifts(cart);
    var isEligible = cartValue >= THRESHOLD_CENTS;
    var targetQty = isEligible ? 1 : 0;

    console.log('[THRESHOLD-GIFT] CHECKOUT VALIDATION - Cart value: $' + (cartValue / 100) + ' | Gift qty:', gift.quantity, '| Target:', targetQty);

    if (gift.quantity > targetQty) {
      await updateCartItem(gift.key, targetQty);
      corrected = true;
    } else if (gift.quantity < targetQty) {
      if (gift.key) {
        await updateCartItem(gift.key, 1);
      } else {
        await addToCart(CONFIG.freeVariantId, 1);
      }
      corrected = true;
    }

    if (corrected) {
      await refreshCartUI();
      showNotification('Tu carrito ha sido ajustado.');
      return false;
    }

    return true;
  }

  window.__checkoutValidators = window.__checkoutValidators || [];
  window.__checkoutValidators.push(validateThresholdGift);

  // ============================================
  // BANNER SYSTEM
  // ============================================

  function injectStyles() {
    if (document.getElementById('threshold-gift-banner-styles')) return;
    var style = document.createElement('style');
    style.id = 'threshold-gift-banner-styles';
    style.textContent =
      '@keyframes threshold-progress-grow{from{width:0%!important}}' +
      '.threshold-gift-banner{padding:16px 20px;margin:24px 0;border-radius:8px;text-align:center;font-size:1.5rem;line-height:1.5;color:#5a4a3a;background:#f7f3ed;border:1px solid #e0d5c5}' +
      '@media screen and (min-width:750px){.threshold-gift-banner{font-size:1.6rem}}' +
      '.threshold-gift-banner p{margin:0 0 12px}' +
      '.threshold-gift-banner__bar{height:12px;background:#c9bda8;border-radius:6px;overflow:hidden}' +
      '.threshold-gift-banner__progress{display:block;height:12px;background:#6a9b4f;border-radius:6px;font-size:0;line-height:0;overflow:hidden;animation:threshold-progress-grow 0.8s cubic-bezier(0.25,0.46,0.45,0.94)}' +
      '.threshold-gift-banner--success{background:#e8f5e2;border-color:#7b9e6b;color:#3d5c2e}' +
      '.threshold-gift-banner--success .threshold-gift-banner__progress{background:#5a8a4a}';
    document.head.appendChild(style);
  }

  function formatMoney(cents) {
    var amount = Math.round(cents / 100);
    return '$' + amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  function buildBannerHtml(cartValue) {
    var remaining = THRESHOLD_CENTS - cartValue;
    var progress = Math.min((cartValue / THRESHOLD_CENTS) * 100, 100);
    var widthPct = progress.toFixed(1);

    if (remaining <= 0) {
      var giftPriceStr = CONFIG.giftPrice
        ? ' de un valor de <strong>' + formatMoney(CONFIG.giftPrice * 100) + '</strong>'
        : '';
      return '<div class="threshold-gift-banner threshold-gift-banner--success">' +
        '<p>Agregamos un regalo gratis' + giftPriceStr + ' a tu carrito.</p>' +
        '<div class="threshold-gift-banner__bar">' +
        '<div class="threshold-gift-banner__progress" style="width:100%">&nbsp;</div>' +
        '</div></div>';
    }

    var remainingStr = formatMoney(remaining);
    var giftPriceStr = CONFIG.giftPrice
      ? ' de un valor de <strong>' + formatMoney(CONFIG.giftPrice * 100) + '</strong>'
      : '';

    return '<div class="threshold-gift-banner">' +
      '<p>Te faltan <strong>' + remainingStr + '</strong> para tener tu banda única de regalo' + giftPriceStr + '!</p>' +
      '<div class="threshold-gift-banner__bar">' +
      '<div class="threshold-gift-banner__progress" style="width:' + widthPct + '%">&nbsp;</div>' +
      '</div></div>';
  }

  function updateBanner() {
    bannerUpdating = true;

    document.querySelectorAll('.threshold-gift-banner').forEach(function(el) { el.remove(); });

    if (!lastCartData || !lastCartData.items || lastCartData.items.length === 0) {
      bannerUpdating = false;
      return;
    }

    var cartValue = getCartValueExcludingGifts(lastCartData);
    var html = buildBannerHtml(cartValue);

    // Cart drawer: before footer
    var drawerFooter = document.querySelector('cart-drawer .drawer__footer');
    if (drawerFooter) {
      drawerFooter.insertAdjacentHTML('beforebegin', html);
    }

    // Cart page: after cart items div
    var cartItemsDiv = document.getElementById('main-cart-items');
    if (cartItemsDiv) {
      cartItemsDiv.insertAdjacentHTML('afterend', html);
    }

    // CSS @keyframes animation fires automatically on insertion — no JS needed
    // Keep bannerUpdating flag up briefly to prevent MutationObserver feedback loop
    setTimeout(function() { bannerUpdating = false; }, 150);
  }

  // ============================================
  // SECTION CHANGE DETECTION
  // Uses MutationObserver on actual DOM elements (not section IDs which
  // have unpredictable template prefixes in Shopify).
  // Dawn's JS captures window.fetch before defer scripts load, so our
  // fetch interceptors miss its cart updates. This catches the section
  // re-render that follows every cart change.
  // ============================================

  function onSectionRerender() {
    if (isProcessing || isSelfUpdate || bannerUpdating) return;
    if (sectionChangeTimer) clearTimeout(sectionChangeTimer);
    sectionChangeTimer = setTimeout(function() {
      console.log('[THRESHOLD-GIFT] Section re-render detected, syncing');
      syncThresholdGift();
    }, 400);
  }

  function setupSectionObservers() {
    // Observe the <cart-items> custom element (cart page)
    var cartItemsEl = document.querySelector('cart-items');
    if (cartItemsEl) {
      new MutationObserver(function() {
        onSectionRerender();
      }).observe(cartItemsEl, { childList: true, subtree: true });
    }

    // Observe the <cart-drawer> custom element
    var drawerEl = document.querySelector('cart-drawer');
    if (drawerEl) {
      new MutationObserver(function() {
        onSectionRerender();
      }).observe(drawerEl, { childList: true, subtree: true });
    }
  }

  // ============================================
  // UI
  // ============================================

  function showNotification(message) {
    var existing = document.getElementById('threshold-gift-notification');
    if (existing) existing.remove();
    var notification = document.createElement('div');
    notification.id = 'threshold-gift-notification';
    notification.innerHTML =
      '<div style="position:fixed;top:20px;left:50%;transform:translateX(-50%);' +
      'background:#ff6b6b;color:white;padding:15px 25px;border-radius:8px;' +
      'box-shadow:0 4px 12px rgba(0,0,0,0.3);z-index:999999;' +
      'font-size:14px;font-weight:500;max-width:90%;text-align:center;">' +
      message + '</div>';
    document.body.appendChild(notification);
    setTimeout(function() { notification.remove(); }, 4000);
  }

  function scheduleSyncDebounced(delay) {
    if (isSelfUpdate) return;
    if (syncTimeout) clearTimeout(syncTimeout);
    syncTimeout = setTimeout(function() { syncThresholdGift(); }, delay || 300);
  }

  // ============================================
  // UI REFRESH (parallel section fetches, no extra getCart)
  // ============================================

  async function refreshCartUI() {
    console.log('[THRESHOLD-GIFT] Refreshing cart UI...');
    try {
      var sectionsToRefresh = [];

      if (document.querySelector('cart-drawer')) {
        sectionsToRefresh.push('cart-drawer');
      }
      if (document.querySelector('cart-items')) {
        sectionsToRefresh.push('main-cart-items');
      }

      var cartFooter = document.querySelector('.cart__footer') ||
                       document.querySelector('#main-cart-footer') ||
                       document.querySelector('[id*="main-cart-footer"]');
      if (cartFooter) {
        var sectionEl = cartFooter.closest('[id^="shopify-section-"]');
        if (sectionEl) {
          sectionsToRefresh.push(sectionEl.id.replace('shopify-section-', ''));
        } else {
          sectionsToRefresh.push('main-cart-footer');
        }
      }

      // Fetch all sections in parallel
      await Promise.all(sectionsToRefresh.map(function(id) {
        return refreshSection(id);
      }));

    } catch (error) {
      console.error('[THRESHOLD-GIFT] Refresh error:', error);
    }
  }

  async function refreshSection(sectionId) {
    try {
      var response = await fetch('/?section_id=' + sectionId, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' }
      });
      var html = await response.text();
      var doc = new DOMParser().parseFromString(html, 'text/html');

      // Use wildcard selector to handle template-prefixed IDs
      var currentSection = document.querySelector('[id$="__' + sectionId + '"]') ||
                          document.getElementById('shopify-section-' + sectionId);

      var newSection = doc.querySelector('[id$="__' + sectionId + '"]') ||
                      doc.getElementById('shopify-section-' + sectionId) ||
                      doc.body.firstElementChild;

      if (currentSection && newSection) {
        currentSection.innerHTML = newSection.innerHTML;
      } else {
        var elementMap = {
          'cart-drawer': 'cart-drawer',
          'main-cart-items': 'cart-items',
          'main-cart-footer': '.cart__footer'
        };
        var selector = elementMap[sectionId];
        if (selector) {
          var currentEl = document.querySelector(selector);
          var newEl = doc.querySelector(selector);
          if (currentEl && newEl) {
            currentEl.innerHTML = newEl.innerHTML;
          }
        }
      }
    } catch (error) {
      console.error('[THRESHOLD-GIFT] Error refreshing section ' + sectionId + ':', error);
    }
  }

  function updateCartBubbleFromData(cart) {
    if (!cart) return;
    document.querySelectorAll('.cart-count-bubble').forEach(function(bubble) {
      var span = bubble.querySelector('span[aria-hidden="true"]');
      if (span) span.textContent = cart.item_count;
    });
  }

  // ============================================
  // EVENT LISTENERS
  // ============================================

  function setupEventListeners() {

    // Intercept XMLHttpRequest
    var originalXHROpen = XMLHttpRequest.prototype.open;
    var originalXHRSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function(method, url) {
      this._url = url;
      return originalXHROpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function() {
      this.addEventListener('load', function() {
        if (isSelfUpdate) return;
        if (this._url && (
          this._url.includes('/cart/add') ||
          this._url.includes('/cart/change') ||
          this._url.includes('/cart/update') ||
          this._url.includes('/cart/clear')
        )) {
          scheduleSyncDebounced(400);
        }
      });
      return originalXHRSend.apply(this, arguments);
    };

    // Intercept fetch API
    var originalFetch = window.fetch;
    window.fetch = function() {
      var args = arguments;
      var url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
      return originalFetch.apply(this, args).then(function(response) {
        if (isSelfUpdate) return response;
        if (url.includes('/cart/add') ||
            url.includes('/cart/change') ||
            url.includes('/cart/update') ||
            url.includes('/cart/clear')) {
          scheduleSyncDebounced(400);
        }
        return response;
      });
    };

    // Native cart events
    document.addEventListener('cart:item-added', function() {
      if (!isSelfUpdate) scheduleSyncDebounced(300);
    });

    document.addEventListener('cart:updated', function() {
      if (!isSelfUpdate && !isProcessing) scheduleSyncDebounced(300);
    });

    // Quantity input changes
    document.addEventListener('change', function(e) {
      if (isSelfUpdate) return;
      if (e.target.closest('quantity-input') ||
          e.target.name === 'updates[]' ||
          e.target.classList.contains('quantity__input')) {
        scheduleSyncDebounced(500);
      }
    });

    // Cart button clicks
    document.addEventListener('click', function(e) {
      if (isSelfUpdate) return;
      var target = e.target;
      if (target.closest('button[name="plus"]') ||
          target.closest('button[name="minus"]') ||
          target.closest('.quantity__button') ||
          target.closest('cart-remove-button') ||
          target.closest('.cart-item__remove')) {
        scheduleSyncDebounced(600);
      }
      if (target.closest('[type="submit"][name="add"]') ||
          target.closest('.product-form__submit') ||
          target.closest('button[data-add-to-cart]')) {
        scheduleSyncDebounced(800);
      }
    });

    // Cart page: poll as reliable fallback. Keys on a content signature
    // (not cart.token, which never changes between item updates) so it
    // actually detects every add/remove/quantity change Dawn makes.
    if (window.location.pathname.includes('/cart')) {
      setInterval(async function() {
        if (isProcessing || isSelfUpdate) return;
        var cart = await getCart();
        if (!cart) return;
        var sig = cartSignature(cart);
        if (sig !== lastCartSignature) {
          lastCartSignature = sig;
          lastCartToken = cart.token;
          lastCartData = cart;
          updateBanner();

          var gift = getThresholdGiftInCart(cart);
          var cartValue = getCartValueExcludingGifts(cart);
          var targetQty = cartValue >= THRESHOLD_CENTS ? 1 : 0;

          if (gift.quantity !== targetQty || gift.rogueItems.length > 0) {
            syncThresholdGift();
          }
        }
      }, 750);
    }
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  function init() {
    console.log('[THRESHOLD-GIFT] Initializing...');

    injectStyles();
    setupEventListeners();
    setupSectionObservers();
    syncThresholdGift();

    document.addEventListener('visibilitychange', function() {
      if (document.visibilityState === 'visible' && !isSelfUpdate) {
        scheduleSyncDebounced(500);
      }
    });

    console.log('[THRESHOLD-GIFT] Ready.');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.ThresholdGiftPromo = {
    sync: syncThresholdGift,
    validate: validateThresholdGift,
    refresh: refreshCartUI,
    getCart: getCart,
    updateBanner: updateBanner
  };

})();